import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Row } from '@tanstack/react-table';
import type { Candidate } from '@/models/job';
import { canSendInterview } from '@/utils/candidate-email';
import { useDeleteCandidate, useJobPipeline } from '@/hooks/useJobOffers';
import { useStageDisplayLabel } from '@/hooks/useStageDisplayLabel';
import { ConfirmationDialog } from '@/components/ui/ConfirmationDialog';
import { PaginationBar } from '@/components/ui';
import { DataTableOrganism } from '@/components/ui/organisms';
import { dataTableFooterPaginationClass } from '@/components/ui/molecules/DataTable/DataTable.styles';
import { cn } from '@/utils/cn';
import { IllustratedEmptyState } from '@/components/ui/molecules';
import { Search } from 'lucide-react';
import { ExtendInterviewModal } from '../ExtendInterviewModal';
import {
  createCandidatesTableColumns,
  mapCandidateToRow,
  sourceLabelKey,
  type CandidatesTableRow,
} from './candidatesTable.columns';
import { emptyStateKeys, filterCandidates, sortCandidates } from './candidatesTable.utils';
import type { CandidatesTableProps } from './CandidatesTable.types';
import { CandidatesTableEmptyStates } from './CandidatesTableEmptyStates';
import { CandidatesToolbar } from '../candidates-toolbar';
import { CreatePipelineStageControl } from '../CreatePipelineStageControl';
import {
  createStageResolver,
  getCandidateStageCounts,
  isPendingStageSemantic,
  sortStagesByPosition,
} from '@/utils/pipeline-stages';

export type { CandidatesTableProps } from './CandidatesTable.types';

export function CandidatesTable({
  candidates,
  isLoading,
  activeStageId,
  preselectCandidateId,
  onSelectedCandidatesChange,
  isJobClosed = false,
  requiresReview = false,
  examConfig,
  fitScores = {},
  techScores = {},
  softSkillsScores = {},
  cheatingFlags = {},
  onCandidateClick,
  onRefresh,
  job,
  onVisibleCandidatesChange,
  searchTerm: externalSearchTerm,
  debouncedSearchTerm: externalDebouncedSearchTerm,
  onSearchTermChange,
  onDebouncedSearchChange,
  onStageChange,
  bulkActionsSlot,
  candidatesView,
  onCandidatesViewChange,
  onAddCv,
}: CandidatesTableProps) {
  const { t } = useTranslation(['job', 'common', 'candidates']);
  const deleteCandidateMutation = useDeleteCandidate(job.id);
  // Same query (and cache key) the Kanban uses, so stage edits made there show
  // up here without a manual refresh.
  const { data: pipeline, isPending: isPipelinePending } = useJobPipeline(job.id);
  const stages = useMemo(
    () => sortStagesByPosition(pipeline?.stages),
    [pipeline]
  );
  const resolveStageId = useMemo(() => createStageResolver(stages), [stages]);
  const stagesById = useMemo(
    () => new Map(stages.map((stage) => [stage.id, stage])),
    [stages]
  );
  const activeStageType = stagesById.get(activeStageId)?.stageType;
  const getStageLabel = useStageDisplayLabel();
  const getCandidateStageLabel = useCallback(
    (candidate: Candidate) => {
      const stage = stagesById.get(resolveStageId(candidate));
      return stage ? getStageLabel(stage) : '';
    },
    [stagesById, resolveStageId, getStageLabel]
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [extendModal, setExtendModal] = useState({
    isOpen: false,
    interviewId: '',
    candidateName: '',
    currentExpiration: null as string | null,
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    candidateId: '',
    candidateName: '',
  });

  const searchTerm = externalSearchTerm ?? '';
  const debouncedSearchTerm = externalDebouncedSearchTerm ?? '';
  const isSearchMode = debouncedSearchTerm.trim().length > 0;

  const filteredCandidates = useMemo(
    () =>
      filterCandidates({
        candidates,
        activeStageId,
        resolveStageId,
        debouncedSearchTerm,
        scores: {
          overall: fitScores,
          tech: techScores,
          soft: softSkillsScores,
        },
        getSourceLabel: (source) => t(sourceLabelKey(source)),
        getStageLabel: getCandidateStageLabel,
      }),
    [
      candidates,
      activeStageId,
      resolveStageId,
      debouncedSearchTerm,
      fitScores,
      techScores,
      softSkillsScores,
      t,
      getCandidateStageLabel,
    ]
  );

  // Browse: full pipeline counts. Search: counts from the same cross-stage match set as rows.
  const stageCounts = useMemo(
    () =>
      getCandidateStageCounts(
        isSearchMode ? filteredCandidates : candidates,
        stages
      ),
    [isSearchMode, filteredCandidates, candidates, stages]
  );

  const stageTabs = useMemo(
    () =>
      stages.map((stage) => ({
        id: stage.id,
        label: getStageLabel(stage),
        count: stageCounts[stage.id] ?? 0,
        trackingTarget: stage.stageType,
      })),
    [stages, stageCounts, getStageLabel]
  );

  const sortedCandidates = useMemo(
    () =>
      sortCandidates({
        candidates: filteredCandidates,
        activeStageType,
        isSearchMode,
        overallScores: fitScores,
      }),
    [filteredCandidates, activeStageType, isSearchMode, fitScores]
  );

  const rows = useMemo(
    () =>
      sortedCandidates.map((candidate) => {
        const stage = stagesById.get(resolveStageId(candidate));
        return mapCandidateToRow({
          candidate,
          jobId: job.id,
          fitScore: fitScores[candidate.id] ?? null,
          hasCheatingFlag: Boolean(cheatingFlags[candidate.id]),
          canSelect: canSendInterview(candidate, { requiresReview }),
          stage,
          stageLabel: stage ? getStageLabel(stage) : undefined,
        });
      }),
    [
      sortedCandidates,
      job.id,
      fitScores,
      cheatingFlags,
      requiresReview,
      stagesById,
      resolveStageId,
      getStageLabel,
    ]
  );

  useEffect(() => {
    setSelectedIds([]);
    onSelectedCandidatesChange?.([]);
  }, [activeStageId, onSelectedCandidatesChange]);

  const handledPreselectRef = useRef<string | null>(null);
  useEffect(() => {
    if (!preselectCandidateId || handledPreselectRef.current === preselectCandidateId) {
      return;
    }
    if (!sortedCandidates.some((c) => c.id === preselectCandidateId)) return;
    handledPreselectRef.current = preselectCandidateId;
    setSelectedIds([preselectCandidateId]);
    onSelectedCandidatesChange?.([preselectCandidateId]);
  }, [preselectCandidateId, sortedCandidates, onSelectedCandidatesChange]);

  useEffect(() => {
    onVisibleCandidatesChange?.(sortedCandidates);
  }, [sortedCandidates, onVisibleCandidatesChange]);

  const notifySelection = (ids: string[]) => {
    setSelectedIds(ids);
    onSelectedCandidatesChange?.(ids);
  };

  const columns = useMemo(
    () =>
      createCandidatesTableColumns({
        t,
        isSearchMode,
        activeStageType,
        requiresReview,
        job,
        examConfig,
        onRefresh,
        onExtendInterview: (interviewId, candidateName, currentExpiration) =>
          setExtendModal({
            isOpen: true,
            interviewId,
            candidateName,
            currentExpiration,
          }),
        onDeleteCandidate: (candidateId, candidateName) =>
          setDeleteModal({ isOpen: true, candidateId, candidateName }),
      }),
    [
      t,
      isSearchMode,
      activeStageType,
      requiresReview,
      job,
      examConfig,
      onRefresh,
    ]
  );

  const closeDeleteModal = () =>
    setDeleteModal({ isOpen: false, candidateId: '', candidateName: '' });

  const { messageKey, hintKey } = emptyStateKeys(activeStageType, true);
  const emptyState = (
    <IllustratedEmptyState
      icon={<Search />}
      title={t(messageKey)}
      description={t(hintKey)}
    />
  );

  const toolbar = onStageChange ? (
    <CandidatesToolbar
      features={{
        statusTabs: true,
        search: true,
        filters: false,
        sort: false,
        bulkActions: false,
        viewSwitcher: true,
      }}
      stageTabs={stageTabs}
      isStageTabsLoading={isPipelinePending && stages.length === 0}
      activeStageId={activeStageId}
      onStageChange={onStageChange}
      createStageSlot={
        <CreatePipelineStageControl
          jobId={job.id}
          onCreated={(stage) => onStageChange?.(stage.id)}
        />
      }
      searchTerm={searchTerm}
      onSearchTermChange={onSearchTermChange}
      onDebouncedSearchChange={onDebouncedSearchChange}
      searchPlaceholder={t('job:candidates.search_placeholder')}
      bulkActionsSlot={bulkActionsSlot}
      view={candidatesView}
      onViewChange={onCandidatesViewChange}
    />
  ) : null;

  if (isLoading || (isPipelinePending && stages.length === 0)) {
    return (
      <div className="h-full bg-white flex flex-col overflow-hidden">
        {toolbar}
        <div className="flex-1 flex items-center justify-center">
          <div className="flex items-center">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" />
            <span className="ml-2 text-gray-500 font-body">Loading candidates...</span>
          </div>
        </div>
      </div>
    );
  }

  const showDomainEmpty =
    candidates.length === 0 || (rows.length === 0 && !isSearchMode);

  return (
    <div className="h-full bg-white flex flex-col overflow-hidden">
      {toolbar}

      <div className="flex-1 min-h-0 flex flex-col">
        {requiresReview && isPendingStageSemantic(activeStageType) && (
          <div className="bg-amber-50 p-4 shrink-0">
            <p className="text-sm text-amber-700">
              <strong>Candidate actions are gated:</strong> This job still requires review.
              Complete the missing information to unlock match scores, candidate selection,
              and interview sending.
            </p>
          </div>
        )}

        {showDomainEmpty ? (
          <CandidatesTableEmptyStates
            hasCandidates={candidates.length > 0}
            filteredCount={rows.length}
            activeStageType={activeStageType}
            searchTerm={searchTerm}
            onAddCv={onAddCv}
          />
        ) : (
          <div className="flex-1 min-h-0">
            <DataTableOrganism<CandidatesTableRow>
              columns={columns}
              data={rows}
              pageSize={100}
              fillHeight
              enableSelection
              enableRowSelection={(row) => row.canSelect}
              selectedIds={selectedIds}
              onSelectedIdsChange={notifySelection}
              selectionDisabled={isJobClosed || requiresReview}
              enableSearch={false}
              enableColumnVisibility={false}
              paginationItemLabel="candidates"
              paginationFooterSlot={(table) => (
                <PaginationBar
                  currentPage={table.getState().pagination.pageIndex + 1}
                  totalPages={table.getPageCount() || 1}
                  totalItems={table.getFilteredRowModel().rows.length}
                  itemsPerPage={table.getState().pagination.pageSize}
                  onPageChange={(page) => table.setPageIndex(page - 1)}
                  onItemsPerPageChange={(size) => table.setPageSize(size)}
                  pageSizeOptions={[50, 100, 150, 200]}
                  itemLabel="candidates"
                  summary="showing"
                  hideWhenSinglePage={false}
                  className={cn(dataTableFooterPaginationClass, 'border-dark-invirtus-soft')}
                />
              )}
              dataTableProps={{
                emptyState,
                framed: false,
                onRowClick: (row: Row<CandidatesTableRow>) =>
                  onCandidateClick?.(row.original.candidate),
              }}
            />
          </div>
        )}
      </div>

      <ExtendInterviewModal
        isOpen={extendModal.isOpen}
        onClose={() =>
          setExtendModal({
            isOpen: false,
            interviewId: '',
            candidateName: '',
            currentExpiration: null,
          })
        }
        interviewId={extendModal.interviewId}
        candidateName={extendModal.candidateName}
        currentExpiration={extendModal.currentExpiration}
        onSuccess={() => onRefresh?.()}
      />

      <ConfirmationDialog
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        title={t('table.delete_title')}
        message={
          <span>{t('table.delete_message', { name: deleteModal.candidateName })}</span>
        }
        gtm="DELETE_CANDIDATE"
        origin="candidate_actions"
        buttons={[
          {
            label: t('common:actions.cancel'),
            onClick: closeDeleteModal,
            type: 'outlined',
            intent: 'secondary',
          },
          {
            label: deleteCandidateMutation.isPending
              ? t('table.deleting')
              : t('common:actions.delete'),
            onClick: () => {
              if (deleteCandidateMutation.isPending) return;
              deleteCandidateMutation.mutate(deleteModal.candidateId, {
                onSuccess: () => {
                  toast.success(t('candidates:notifications.deleted_success'));
                  closeDeleteModal();
                },
                onError: (error: Error) => {
                  console.error('Error deleting candidate:', error);
                  toast.error(
                    error.message.includes('403')
                      ? t('candidates:notifications.delete_forbidden')
                      : error.message || t('candidates:notifications.delete_failed')
                  );
                },
              });
            },
            type: 'contained',
            intent: 'danger',
            loading: deleteCandidateMutation.isPending,
          },
        ]}
      />
    </div>
  );
}
